import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, CloseButton, SideDrawer } from '@/components';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { parseApiError } from '@/common/utils';
import { DashboardPage } from '../../components/PremiumDesign';
import editIcon from '../../assets/edit.png';
import { CRMContactDetailPanel } from '../components/CRMContactDetailPanel';
import { CRMContactForm } from '../components/CRMContactForm';
import { CRMTaskModal } from '../components/CRMTaskModal';
import { AppointmentDrawer } from '../../appointments/components/AppointmentDrawer';
import { readAnyContactName } from '../../appointments/utils';
import {
  getCRMContactDetail,
  getCRMContactSources,
  getCRMContactTimeline,
  updateCRMContact,
} from '../api';
import { syncPipelineBoardContact } from '../utils/pipelineSync';
import { decodeId } from '@/common/utils/encryption';

const MANAGEMENT_TAB_IDS = new Set([
  'notes',
  'tasks',
  'follow-ups',
  'attachments',
  'session',
]);

const PAGE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'notes', label: 'Notes' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'follow-ups', label: 'Follow-Ups' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'session', label: 'Session' },
  { id: 'quotations', label: 'Quotations' },
  { id: 'live-chat', label: 'Live Chat' },
];

const CONTACT_ID_STORAGE_KEY = 'crm-contact-overview-id';
const CONTACT_NAME_STORAGE_KEY = 'crm-contact-overview-name';

/**
 * Only these query keys may remain in the address bar after deep-link load.
 * Everything else (contact_id, task_id, note_id, session_id, follow_up_id, …)
 * is treated as a one-time deep-link identifier and stripped automatically —
 * including any future *_id params without code changes.
 */
const ALLOWED_OVERVIEW_QUERY_KEYS = new Set(['tab']);

function buildOverviewTabSearch(tabId) {
  if (!tabId || tabId === 'overview') return '';
  return `?tab=${encodeURIComponent(tabId)}`;
}

function hasDisallowedQueryParams(search) {
  const params = new URLSearchParams(search);
  for (const key of params.keys()) {
    if (!ALLOWED_OVERVIEW_QUERY_KEYS.has(key)) return true;
  }
  return false;
}

// function buildLeadDeepLinkUrls({ contactId, tabId }) {
//   const encodedContactId = encodeURIComponent(encodeId(String(contactId)));
//   const tabQuery =
//     tabId && tabId !== 'overview' ? `&tab=${encodeURIComponent(tabId)}` : '';
//   const origin = typeof window !== 'undefined' ? window.location.origin : '';
//   return {
//     contactId: String(contactId),
//     tabId: tabId || 'overview',
//     raw: `${origin}/crm-contacts/overview?contact_id=${contactId}${tabQuery}`,
//     encoded: `${origin}/crm-contacts/overview?contact_id=${encodedContactId}${tabQuery}`,
//     pathOnlyRaw: `/crm-contacts/overview?contact_id=${contactId}${tabQuery}`,
//     pathOnlyEncoded: `/crm-contacts/overview?contact_id=${encodedContactId}${tabQuery}`,
//     notificationPatterns: {
//       overview: '/crm-contacts/overview?contact_id=:contact_id',
//       tasks:
//         '/crm-contacts/overview?contact_id=:contact_id&tab=tasks&task_id=:task_id',
//       notes:
//         '/crm-contacts/overview?contact_id=:contact_id&tab=notes&note_id=:note_id',
//     },
//   };
// }

function getCompanyId() {
  const stored =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (stored) return String(stored);
  try {
    const userStr =
      localStorage.getItem('user') || sessionStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      const cid = user?.company_id || user?.company?.id || user?.id;
      if (cid) return String(cid);
    }
  } catch {
    // ignore
  }
  return '';
}

function resolveContactId(locationState, locationSearch) {
  const fromState = locationState?.contactId;
  if (fromState != null && String(fromState).trim()) {
    return String(fromState);
  }

  if (locationSearch) {
    const searchParams = new URLSearchParams(locationSearch);
    const encodedId = searchParams.get('contact_id');
    if (encodedId) {
      const decoded = decodeId(encodedId);
      if (decoded && !isNaN(Number(decoded))) {
        return decoded;
      } else if (!isNaN(Number(encodedId))) {
        return encodedId;
      }
    }
  }

  try {
    const stored = sessionStorage.getItem(CONTACT_ID_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // ignore
  }
  return '';
}

function persistOverviewContact({ contactId, contactName }) {
  try {
    if (contactId) sessionStorage.setItem(CONTACT_ID_STORAGE_KEY, contactId);
    if (contactName != null) {
      sessionStorage.setItem(CONTACT_NAME_STORAGE_KEY, contactName);
    }
    window.dispatchEvent(new Event('crm-contact-overview-updated'));
  } catch {
    // ignore
  }
}

export function CRMContactOverviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddingFollowUp, setIsAddingFollowUp] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isTaskOpen, setIsTaskOpen] = useState(false);
  const [isMeetingOpen, setIsMeetingOpen] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const tabsContainerRef = useRef(null);

  useEffect(() => {
    const scrollActiveTabToCenter = (behavior = 'smooth') => {
      if (!tabsContainerRef.current) return;
      const container = tabsContainerRef.current;
      const activeBtn = container.querySelector('[data-active="true"]');
      if (!activeBtn) return;

      const containerWidth = container.offsetWidth;
      const btnLeft = activeBtn.offsetLeft;
      const btnWidth = activeBtn.offsetWidth;
      const targetScrollLeft = btnLeft - containerWidth / 2 + btnWidth / 2;

      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior,
      });
    };

    const timer = setTimeout(() => {
      scrollActiveTabToCenter('smooth');
    }, 50);

    return () => clearTimeout(timer);
  }, [activeTab]);

  const contactId = useMemo(
    () => resolveContactId(location.state, location.search),
    [location.state, location.search],
  );

  const currentCompanyId = getCompanyId();
  const canUpdateLeads = can(PERMISSIONS.LEADS.UPDATE);
  const canCreateLeadManagement = can(PERMISSIONS.LEAD_MANAGEMENT.CREATE);
  const canCreateAppointment = can(PERMISSIONS.APPOINTMENTS.CREATE);
  const canViewLeadManagement = can(PERMISSIONS.LEAD_MANAGEMENT.VIEW);
  const canAccessLiveChat =
    can(PERMISSIONS.WA_CHATS.CREATE) || can(PERMISSIONS.WA_CHATS.VIEW);
  const canViewQuotations = can(PERMISSIONS.QUOTATIONS.VIEW);

  const visibleTabs = useMemo(
    () =>
      PAGE_TABS.filter((tab) => {
        if (tab.id === 'live-chat') return canAccessLiveChat;
        if (tab.id === 'quotations') return canViewQuotations;
        if (MANAGEMENT_TAB_IDS.has(tab.id)) return canViewLeadManagement;
        return true;
      }),
    [canViewLeadManagement, canAccessLiveChat, canViewQuotations],
  );

  const defaultTabId = visibleTabs[0]?.id || 'overview';

  const selectTab = (tabId) => {
    if (!visibleTabs.some((tab) => tab.id === tabId)) return;
    setActiveTab(tabId);
    if (contactId) {
      // console.log(
      //   '[Leads deep-link URLs] overview tab change',
      //   buildLeadDeepLinkUrls({ contactId, tabId }),
      // );
    }
    navigate(
      {
        pathname: location.pathname,
        search: buildOverviewTabSearch(tabId),
      },
      { replace: true, state: location.state },
    );
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const requestedTab = tabParam || 'overview';
    const isAllowed = visibleTabs.some((tab) => tab.id === requestedTab);

    if (!isAllowed) {
      setActiveTab(defaultTabId);
      if (tabParam) {
        navigate(
          {
            pathname: location.pathname,
            search: buildOverviewTabSearch(defaultTabId),
          },
          { replace: true, state: location.state },
        );
      }
      return;
    }

    setActiveTab(requestedTab);
  }, [
    location.search,
    location.pathname,
    location.state,
    visibleTabs,
    defaultTabId,
    navigate,
  ]);

  useEffect(() => {
    if (!contactId) {
      navigate('/crm-contacts', { replace: true });
      return;
    }
    const nameFromState = location.state?.contactName;
    persistOverviewContact({
      contactId,
      contactName: nameFromState || undefined,
    });
    // console.log(
    //   '[Leads deep-link URLs] overview page (hidden from address bar)',
    //   {
    //     visibleUrl: `${window.location.origin}${location.pathname}${location.search}`,
    //     ...buildLeadDeepLinkUrls({
    //       contactId,
    //       tabId: new URLSearchParams(location.search).get('tab') || 'overview',
    //     }),
    //   },
    // );
  }, [contactId, location.state, location.pathname, location.search, navigate]);

  const { data: contactDetails, isLoading: isContactDetailsLoading } = useQuery(
    {
      queryKey: ['crm-contact-detail', currentCompanyId, contactId],
      queryFn: () =>
        getCRMContactDetail({
          companyId: currentCompanyId,
          contactId,
        }),
      enabled: !!currentCompanyId && !!contactId,
    },
  );

  // After deep-link params are used and contact data loads, rebuild the URL
  // with only allowed public params (tab). Any other key is stripped.
  // Uses navigate({ replace: true }) so React Router stays in sync without a reload.
  useEffect(() => {
    if (!contactId || isContactDetailsLoading || !contactDetails) return;
    if (!hasDisallowedQueryParams(location.search)) return;

    // Ensure sessionStorage has contactId before URL loses contact_id
    persistOverviewContact({
      contactId,
      contactName: contactDetails?.name || location.state?.contactName,
    });

    const tabParam = new URLSearchParams(location.search).get('tab');
    const tabToKeep =
      tabParam && visibleTabs.some((tab) => tab.id === tabParam)
        ? tabParam
        : activeTab;

    navigate(
      {
        pathname: location.pathname,
        search: buildOverviewTabSearch(tabToKeep),
      },
      { replace: true, state: location.state },
    );
  }, [
    contactId,
    contactDetails,
    isContactDetailsLoading,
    location.search,
    location.pathname,
    location.state,
    visibleTabs,
    activeTab,
    navigate,
  ]);

  const { mutate: handleUpdateContact, isPending: isUpdatingContact } =
    useMutation({
      mutationFn: updateCRMContact,
      onSuccess: (data, variables) => {
        toast.success('Lead updated successfully!');
        setApiErrors({});
        setIsEditDrawerOpen(false);
        queryClient.invalidateQueries({
          queryKey: ['crm-contact-detail', currentCompanyId, contactId],
        });
        queryClient.invalidateQueries({
          queryKey: ['crm-contacts'],
        });
        queryClient.invalidateQueries({
          queryKey: ['crm-sources-stats'],
        });
        syncPipelineBoardContact(queryClient, contactId, {
          ...(data || {}),
          ...(variables?.payload || {}),
        });
      },
      onError: (error) => {
        const { message, fieldErrors } = parseApiError(error);
        setApiErrors(fieldErrors);
        toast.error(message || 'Failed to update lead.');
      },
    });

  const editableContact = useMemo(
    () => (contactDetails ? { id: contactId, ...contactDetails } : null),
    [contactDetails, contactId],
  );

  const openEditDrawer = () => {
    setApiErrors({});
    setIsEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    if (isUpdatingContact) return;
    setApiErrors({});
    setIsEditDrawerOpen(false);
  };

  const submitContactUpdate = (payload) => {
    handleUpdateContact({
      companyId: currentCompanyId,
      contactId,
      payload,
    });
  };

  useEffect(() => {
    if (contactDetails?.name) {
      persistOverviewContact({
        contactId,
        contactName: contactDetails.name,
      });
    }
  }, [contactDetails?.name, contactId]);

  const { data: contactSources, isLoading: isContactSourcesLoading } = useQuery(
    {
      queryKey: ['crm-contact-sources', currentCompanyId, contactId],
      queryFn: async () => {
        if (!currentCompanyId || !contactId) return [];
        return getCRMContactSources({
          companyId: currentCompanyId,
          contactId,
        });
      },
      enabled: !!currentCompanyId && !!contactId,
    },
  );

  // Keep the CRM Pipeline Board in sync whenever contact details or sources are updated
  useEffect(() => {
    if (!contactId || !contactDetails) return;
    syncPipelineBoardContact(queryClient, contactId, {
      ...contactDetails,
      sources: contactSources,
    });
  }, [contactDetails, contactSources, contactId, queryClient]);

  const {
    data: contactTimeline,
    isLoading: isContactTimelineLoading,
    fetchNextPage: fetchNextTimelinePage,
    hasNextPage: hasNextTimelinePage,
    isFetchingNextTimelinePage,
  } = useInfiniteQuery({
    queryKey: ['crm-contact-timeline', currentCompanyId, contactId],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      if (!currentCompanyId || !contactId) return { results: [] };
      return getCRMContactTimeline({
        companyId: currentCompanyId,
        contactId,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.next) {
        try {
          const url = new URL(lastPage.next, window.location.origin);
          const page = url.searchParams.get('page');
          if (page) return Number(page);
        } catch {
          const match = String(lastPage.next).match(/[?&]page=(\d+)/);
          if (match && match[1]) return Number(match[1]);
        }
        return (allPages?.length || 1) + 1;
      }
      return undefined;
    },
    enabled: !!currentCompanyId && !!contactId,
  });

  const safeTagsList = useMemo(() => {
    try {
      if (!contactDetails?.tags) return [];
      const raw = contactDetails.tags;
      return Array.isArray(raw)
        ? raw.map((t) => (typeof t === 'object' ? t.name : String(t)))
        : String(raw)
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
    } catch {
      return [];
    }
  }, [contactDetails]);

  const safeRender = (val) => {
    if (val == null) return '-';
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val, null, 2);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  const handleBack = () => {
    if (contactId && contactDetails) {
      syncPipelineBoardContact(queryClient, contactId, {
        ...contactDetails,
        sources: contactSources,
      });
    }
    const from = location.state?.from;
    if (typeof from === 'string' && from.startsWith('/crm-pipeline')) {
      if (window.history.length > 1 && location.key !== 'default') {
        navigate(-1);
        return;
      }
      navigate('/crm-pipeline');
      return;
    }
    if (typeof from === 'string' && from.startsWith('/calendar')) {
      if (window.history.length > 1 && location.key !== 'default') {
        navigate(-1);
        return;
      }
      navigate('/calendar');
      return;
    }
    if (from) {
      navigate(from);
    } else if (window.history.length > 1 && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/crm-contacts');
    }
  };

  if (!contactId) return null;

  return (
    <DashboardPage className="!bg-transparent !p-0 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="lead-detail w-full h-full min-h-0 flex flex-col gap-4 rounded-[16px] bg-white border border-[#EBF4EA] p-4 sm:p-5 overflow-hidden">
        {/* Top bar: back + tabs (Figma) */}
        {!isAddingFollowUp && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 sm:gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center justify-center w-10 h-10 shrink-0 rounded-[8px] bg-white border border-[#50AB3D] text-[#50AB3D] hover:bg-[#EBF4EA] transition-colors focus:outline-none"
              aria-label="Back"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.25}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div
              ref={tabsContainerRef}
              className="relative flex min-w-0 flex-1 items-center gap-2 overflow-x-auto no-scrollbar sm:gap-3"
            >
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    data-active={isActive ? 'true' : 'false'}
                    onClick={() => selectTab(tab.id)}
                    className={`h-10 px-4 sm:px-5 rounded-[8px] text-md font-medium whitespace-nowrap transition-colors focus:outline-none shrink-0 ${
                      isActive
                        ? 'bg-[#50AB3D] text-white'
                        : 'bg-[#F5F5F5] text-[#868686] hover:text-[#1F2241]'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {activeTab === 'overview' && (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {canCreateLeadManagement && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setIsTaskOpen(true)}
                    disabled={isContactDetailsLoading}
                    className="!h-10 !px-3 text-sm rounded-[8px]"
                  >
                    + Task
                  </Button>
                )}
                {canCreateAppointment && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setIsMeetingOpen(true)}
                    disabled={isContactDetailsLoading}
                    className="!h-10 !px-3 text-sm rounded-[8px]"
                  >
                    + Meeting
                  </Button>
                )}
                {canUpdateLeads && (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={openEditDrawer}
                    disabled={isContactDetailsLoading || !editableContact}
                    className="!h-10 !w-10 sm:!w-auto shrink-0 gap-1.5 !px-0 sm:!px-4 text-body-lg font-medium rounded-[8px]"
                    aria-label="Edit"
                  >
                    <img
                      src={editIcon}
                      alt=""
                      className="h-5 w-5 object-contain shrink-0"
                    />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div
          className={`flex-1 min-h-0 ${
            activeTab === 'live-chat' ||
            activeTab === 'notes' ||
            activeTab === 'tasks' ||
            activeTab === 'follow-ups' ||
            activeTab === 'attachments'
              ? 'overflow-hidden flex flex-col'
              : 'overflow-y-auto custom-scrollbar'
          }`}
        >
          <CRMContactDetailPanel
            variant="page"
            activeTab={activeTab}
            contactToView={{ id: contactId, ...contactDetails }}
            contactDetails={contactDetails}
            isContactDetailsLoading={isContactDetailsLoading}
            companyId={currentCompanyId}
            tagsData={contactDetails?.tags}
            safeTagsList={safeTagsList}
            safeRender={safeRender}
            contactSources={contactSources}
            isContactSourcesLoading={isContactSourcesLoading}
            contactTimeline={contactTimeline}
            isContactTimelineLoading={isContactTimelineLoading}
            fetchNextTimelinePage={fetchNextTimelinePage}
            hasNextTimelinePage={hasNextTimelinePage}
            isFetchingNextTimelinePage={isFetchingNextTimelinePage}
            onViewAllFollowUps={
              canViewLeadManagement ? () => selectTab('follow-ups') : undefined
            }
            onFollowUpCreatingChange={setIsAddingFollowUp}
          />
        </div>
      </div>

      <SideDrawer
        isOpen={isEditDrawerOpen}
        onClose={closeEditDrawer}
        width="xl"
        zIndexClass="z-[60]"
        showClose={false}
        bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
        headerClassName="!px-6 !py-5"
        header={
          <div className="flex w-full items-center justify-between gap-3">
            <h2 className="text-[20px] leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display">
              Update Lead
            </h2>
            <CloseButton
              size="md"
              onClick={closeEditDrawer}
              disabled={isUpdatingContact}
              className="!rounded-[8px]"
            />
          </div>
        }
      >
        <CRMContactForm
          companyId={currentCompanyId}
          entity="lead"
          initialContact={editableContact}
          isPending={isUpdatingContact}
          apiErrors={apiErrors}
          onSubmit={submitContactUpdate}
        />
      </SideDrawer>

      <CRMTaskModal
        isOpen={isTaskOpen}
        onClose={() => setIsTaskOpen(false)}
        companyId={currentCompanyId}
        contactId={contactId}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ['crm-contact-timeline', currentCompanyId, contactId],
          });
          queryClient.invalidateQueries({
            queryKey: ['crm-contact-tasks', currentCompanyId, contactId],
          });
        }}
      />

      <AppointmentDrawer
        isOpen={isMeetingOpen}
        onClose={() => setIsMeetingOpen(false)}
        mode="create"
        companyId={currentCompanyId}
        initialContact={{
          id: contactId,
          label: readAnyContactName(contactDetails) || 'Current contact',
          assignedEmployeeId:
            contactDetails?.assigned_to_employee_id ||
            contactDetails?.assigned_to_id ||
            contactDetails?.assignee_id ||
            '',
        }}
        onCreated={() => {
          queryClient.invalidateQueries({
            queryKey: ['crm-contact-timeline', currentCompanyId, contactId],
          });
        }}
      />
    </DashboardPage>
  );
}
